import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  mainBlueColor,
  inputPriceState,
  additionalCostData,
  inputAdditionalCostState,
  inputAdditionalCostVenderState,
  workingBudgetVendor,
  workingBudgetInfo,
  btnDisabledFlg
} from '../../GlobalState';
import { getWorkingBudgetInfo } from '../../apicalls/WorkingBudgetPage';
import _ from 'lodash';
import { CustomTooltip } from '../../components/tooltip/CustomTooltip';
import { tooltipContents } from '../../components/tooltip/Const';
import HelpIcon from '@material-ui/icons/Help';
import { NOT_LOGGED_IN } from '../../const/Const';
import { TypeWorkingBudgetInfo } from '../../types/WorkingBudgetPage';
import { calculationTotallingInfo } from '../../utils/WorkingBudgetPage';

type TProps = {
  workingBudgetId: number;
  changeLoggedInData: (newData: string) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * 実行予算結果
 * @constructor
 */
export const WorkingBudget: React.FC<TProps> = (props: TProps) => {
  const { workingBudgetId, changeLoggedInData, setErrorMessage, setOpen } =
    props;

  const [mainColor] = useRecoilState(mainBlueColor);
  const [vendor] = useRecoilState(workingBudgetVendor);
  const [, setDisabledFlg] = useRecoilState(btnDisabledFlg);
  const [totallingInfo, setTotallingInfo] = useRecoilState(workingBudgetInfo);
  const [additionalCost, setAdditionalCost] =
    useRecoilState(additionalCostData);

  const classes = useStyles({ mainColor: mainColor });

  useEffect(() => {
    getWorkingBudgetInfo(workingBudgetId).then(data => {
      if (data.data === NOT_LOGGED_IN) {
        changeLoggedInData(NOT_LOGGED_IN);
      } else {
        if (typeof data.error === 'string') {
          setErrorMessage(data.error);
          setOpen(true);
        } else {
          setDisabledFlg(false);
          setTotallingInfo({
            estimate_total: data.workingBudgetInfo.estimate_total,
            estimate_cost_total: data.workingBudgetInfo.estimate_cost_total,
            gross_profit: data.workingBudgetInfo.gross_profit,
            gross_profit_margin: data.workingBudgetInfo.gross_profit_margin,
            buffer: data.workingBudgetInfo.buffer,
            additional_cost: data.workingBudgetInfo.additional_cost,
            final_gross_profit: data.workingBudgetInfo.final_gross_profit,
            final_gross_profit_margin:
              data.workingBudgetInfo.final_gross_profit_margin,
            order_total: data.workingBudgetInfo.order_total
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (vendor) {
      const cloneTotallingInfo = _.cloneDeep(totallingInfo);
      if (cloneTotallingInfo) {
        calculationTotallingInfo(
          vendor,
          additionalCost,
          cloneTotallingInfo,
          setTotallingInfo
        );
      }
    }
  }, [vendor, additionalCost]);

  return (
    <div className={classes.container}>
      <div className={classes.sub}>
        <div className={classes.estimatedTotalAmount}>
          <div className={classes.title}>見積合計額</div>
          <div className={classes.estimatedTotalAmountVal}>
            {totallingInfo ? totallingInfo.estimate_total.toLocaleString() : 0}
            円
          </div>
        </div>
        <div className={classes.othersItem}>
          <div className={classes.row}>
            <div className={classes.rowItem}>
              <div className={classes.title}>見積原価合計額</div>
              <div className={classes.val}>
                {totallingInfo
                  ? totallingInfo.estimate_cost_total.toLocaleString()
                  : 0}
                円
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>粗利金額</div>
              <div className={classes.val}>
                {totallingInfo
                  ? totallingInfo.gross_profit.toLocaleString()
                  : 0}
                円
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>粗利率</div>
              <div className={classes.val}>
                {totallingInfo ? totallingInfo.gross_profit_margin : 0}%
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>
                未発注総額
                <CustomTooltip title={tooltipContents.Buffer} placement='right'>
                  <HelpIcon style={{ marginLeft: 8 }} />
                </CustomTooltip>
              </div>
              <div className={classes.val}>
                {totallingInfo ? totallingInfo.buffer.toLocaleString() : 0}円
              </div>
            </div>
          </div>
          <div className={classes.row}>
            <div className={classes.rowItem}>
              <div className={classes.title}>
                追加原価合計額
                <CustomTooltip
                  title={tooltipContents.AdditionalCost}
                  placement='right'
                >
                  <HelpIcon style={{ marginLeft: 8 }} />
                </CustomTooltip>
              </div>
              <div className={classes.val}>
                {totallingInfo
                  ? totallingInfo.additional_cost.toLocaleString()
                  : 0}
                円
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>
                最終粗利金額
                <CustomTooltip
                  title={tooltipContents.FinalGrossProfit}
                  placement='right'
                >
                  <HelpIcon style={{ marginLeft: 8 }} />
                </CustomTooltip>
              </div>
              <div className={classes.val}>
                {totallingInfo
                  ? totallingInfo.final_gross_profit.toLocaleString()
                  : 0}
                円
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>
                最終粗利率
                <CustomTooltip
                  title={tooltipContents.FinalGrossProfitMargin}
                  placement='right'
                >
                  <HelpIcon style={{ marginLeft: 8 }} />
                </CustomTooltip>
              </div>
              <div className={classes.val}>
                {totallingInfo ? totallingInfo.final_gross_profit_margin : 0}%
              </div>
            </div>
            <div className={classes.rowItem}>
              <div className={classes.title}>
                発注総額
                <CustomTooltip
                  title={tooltipContents.OrderTotal}
                  placement='right'
                >
                  <HelpIcon style={{ marginLeft: 8 }} />
                </CustomTooltip>
              </div>
              <div className={classes.val}>
                {totallingInfo ? totallingInfo.order_total.toLocaleString() : 0}
                円
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type TStyleProps = {
  mainColor: string;
};

const useStyles = makeStyles<Theme, TStyleProps>((theme: Theme) =>
  createStyles({
    container: {
      padding: 10,
      display: 'flex',
      justifyContent: 'flex-end',
      width: '70%'
    },
    sub: {
      display: 'flex',
      width: '90%'
    },
    estimatedTotalAmount: {
      display: 'flex',
      flexDirection: 'column',
      height: 200,
      width: '20%'
    },
    estimatedTotalAmountVal: {
      display: 'flex',
      // flexDirection: 'column',
      height: 150,
      justifyContent: 'right',
      alignItems: 'center',
      border: '1px #CCCCCC solid',
      paddingRight: 20
    },
    othersItem: {
      display: 'flex',
      flexDirection: 'column',
      height: 200,
      width: '80%'
    },
    row: {
      display: 'flex'
    },
    rowItem: {
      display: 'flex',
      flexDirection: 'column',
      width: '25%',
      height: 100
    },
    title: {
      display: 'flex',
      // flexDirection: 'column',
      height: 50,
      justifyContent: 'right',
      alignItems: 'center',
      backgroundColor: props => props.mainColor,
      color: '#FFFFFF',
      fontSize: 14,
      border: '1px #CCCCCC solid',
      paddingRight: 20
    },
    val: {
      display: 'flex',
      height: 50,
      justifyContent: 'right',
      alignItems: 'center',
      border: '1px #CCCCCC solid',
      paddingRight: 20
    }
  })
);
